// FLAGS: silent-failure
// Display formatting for names, session dates and scores. Two conventions sit side by side here: the week and
// day helpers work in UTC, while the zoned formatters take an explicit timeZone argument — see the module doc.
import moment, { Moment } from "moment";
import {
  HELP_NEEDED_VALUES,
  NEXT_STEPS,
  ON_SITE_SESSION_TYPES,
  QUESTIONS_KEYS,
} from "@src/constants";
import { upperFirst } from "lodash";
import { format, toZonedTime } from "date-fns-tz";
import { DateFnslocaleMapper } from "@src/DateLocaleSetup";
import { TFunction } from "i18next";
import { formatDistanceToNow, isAfter, subDays } from "date-fns";

export const formatUserName = (
  user: {
    firstName: string;
    lastName: string | null | undefined;
    yearOfBirth: number | null;
  },
  showExtraData = false,
) => {
  if (!user) {
    return undefined;
  }
  const { firstName, lastName, yearOfBirth } = user;

  let formattedUserName = lastName ? `${lastName}, ${firstName}` : firstName;

  if (showExtraData) {
    formattedUserName = `${formattedUserName} (${yearOfBirth ?? "-"})`;
  }

  return formattedUserName;
};

export const formatFirstUpperCase = (word: string | null | undefined) => {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : "";
};

export const formatNameAvatar = (
  firstName: string,
  lastName: string | null | undefined,
) => {
  return (
    firstName.charAt(0).toUpperCase() +
    (lastName ? lastName.charAt(0).toUpperCase() : "")
  );
};

export const formatLanguage = (shortTermLanguage: string) => {
  switch (shortTermLanguage) {
    case "EN":
      return "English";
    case "NL":
      return "Netherlands";
    default:
      return "English";
  }
};

export const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";

export const formatSessionDateForSessionDetails = (consult: {
  startedAt: string;
}) => {
  return moment(consult.startedAt, dateFormat)
    .tz(moment.tz.guess())
    .format("HH:mm - DD-MM-YYYY");
};

// Filters restored from localStorage carry dates as JSON strings even though
// they are typed as Date — normalize both shapes to an ISO string.
export const toIsoString = (date?: Date | string): string | undefined =>
  date ? new Date(date).toISOString() : undefined;

export const formatStandardDate = (date: Date | string, timeZone: string) => {
  const zonedDate = toZonedTime(date, timeZone);
  return format(zonedDate, "dd-MM-yy HH:mm", {
    locale: DateFnslocaleMapper(),
  });
};

export const formatStandardDateOnly = (
  date: Date | string,
  timeZone: string,
) => {
  const zonedDate = toZonedTime(date, timeZone);
  return format(zonedDate, "dd-MM-yy", {
    locale: DateFnslocaleMapper(),
  });
};

export const formatTimeOnly = (date: Date | string, timeZone: string) => {
  const zonedDate = toZonedTime(date, timeZone);
  return format(zonedDate, "HH:mm", {
    locale: DateFnslocaleMapper(),
  });
};

export const formatClientName = (client: {
  firstName: string;
  lastName: string | null | undefined;
}) => {
  const lastName = client.lastName ? ` ${client.lastName}` : "";
  return `${client.firstName}${lastName}`;
};

export const formatConsultDateExtendedWritten = (consult: {
  startedAt: string;
}) => {
  return moment(consult.startedAt, dateFormat)
    .tz(moment.tz.guess())
    .format("MMMM D, YYYY");
};

// FLAGS: silent-failure
// moveForwardWeeks, moveBackwardWeeks, formatDateWithMonth and getWeekStartQueryKey below all work in UTC,
// ignoring the timezone the booking picker has selected. The picker's week window and its availability cache
// keys come from them, which is one half of the UTC day cut-off recorded in views/sessions/MODULE.md — a caller
// west of UTC gets the wrong week boundary and nothing fails.
export const moveForwardWeeks = (weeks: number) => {
  return moment().utc().startOf("isoWeek").add(weeks, "weeks");
};

export const moveBackwardWeeks = (weeks: number) => {
  return moment().utc().endOf("isoWeek").add(weeks, "weeks");
};

export const formatWeek = (time: Moment) => {
  return time.format("D MMM");
};

export const formatWeekWithYear = (time: Moment) => {
  return time.format("D MMM YYYY");
};

export const getYearTime = (time: string | Moment) => {
  return moment(time).year();
};

export const getWeekday = (time: string | Moment) => {
  return moment(time).format("ddd");
};

export const formatDateWithMonth = (time: string | Moment) => {
  return moment(time).utc().format("D MMM");
};

export const getWeekStartQueryKey = (dateTime: string | Moment) =>
  moment(dateTime).utc().startOf("isoWeek").startOf("day").toISOString();

export const helpNeededFormatting = (
  t: TFunction,
  helpValue: keyof typeof HELP_NEEDED_VALUES | undefined,
) => {
  switch (helpValue) {
    case HELP_NEEDED_VALUES.TRUE: {
      return t("Consults.SessionDetails.HelpClient.Yes");
    }
    case HELP_NEEDED_VALUES.NO_REFER_TO_IPRACTICE: {
      return t("Consults.SessionDetails.HelpClient.noReferToiPractice");
    }
    case HELP_NEEDED_VALUES.NO_REFER_TO_OTHER_PROVIDER: {
      return t("Consults.SessionDetails.HelpClient.noReferToOtherProvider");
    }
    case HELP_NEEDED_VALUES.NO_CLIENT_AT_RISK: {
      return t("Consults.SessionDetails.HelpClient.ClientAtRisk");
    }
    default: {
      return t("Consults.SessionDetails.HelpClient.NoInput");
    }
  }
};

export const handleNextStepsText = (
  t: TFunction,
  nextSteps: keyof typeof NEXT_STEPS | undefined,
) => {
  switch (nextSteps) {
    case NEXT_STEPS.WEEKS: {
      return t("Consults.SessionDetails.NextSteps.Weeks");
    }
    case NEXT_STEPS.MONTHS: {
      return t("Consults.SessionDetails.NextSteps.Months");
    }
    case NEXT_STEPS.BREAK: {
      return t("Consults.SessionDetails.NextSteps.Break");
    }
    default: {
      return t("Consults.SessionDetails.HelpClient.NoInput");
    }
  }
};
const THEME_KEYS = {
  CONFIDENCE: "Confidence",
  STRESS: "Stress",
  WORK: "Work",
  PURPOSE: "Purpose",
  RELATIONSHIPS: "Relationships",
  HEALTH: "Health",
  SLEEP: "Sleep",
  MINDFULNESS: "Mindfulness",
  MEDICAL: "medical",
};
export const themeValue = (value: keyof typeof QUESTIONS_KEYS | undefined) => {
  switch (value) {
    case QUESTIONS_KEYS.CONFIDENCE:
      return THEME_KEYS.CONFIDENCE;
    case QUESTIONS_KEYS.STRESS:
      return THEME_KEYS.STRESS;
    case QUESTIONS_KEYS.WORK:
      return THEME_KEYS.WORK;
    case QUESTIONS_KEYS.PURPOSE:
      return THEME_KEYS.PURPOSE;
    case QUESTIONS_KEYS.RELATIONSHIPS:
      return THEME_KEYS.RELATIONSHIPS;
    case QUESTIONS_KEYS.HEALTH:
      return THEME_KEYS.HEALTH;
    case QUESTIONS_KEYS.SLEEP:
      return THEME_KEYS.SLEEP;
    case QUESTIONS_KEYS.MINDFULNESS:
      return THEME_KEYS.MINDFULNESS;
    case QUESTIONS_KEYS.MEDICAL:
      return THEME_KEYS.MEDICAL;
    default:
      return null;
  }
};

type TranslateWithDefault = (
  key: string,
  options: { defaultValue: string },
) => string;

// The translated SessionType.<type> label, or the capitalised type name when the key is missing (medical has none).
export const formatSessionType = (
  t: TranslateWithDefault,
  consultType: string | null | undefined,
) =>
  consultType
    ? t(`SessionType.${consultType}`, { defaultValue: upperFirst(consultType) })
    : undefined;

type TranslateWithOptions = (
  key: string,
  options: Record<string, unknown>,
) => string;

// The session-type line on the booking time-slot step. Video sessions share one line that states the call length;
// an on-site session has its own Booking.TimeSlot.SessionType.<type> line because that length doesn't apply to it.
export const formatTimeSlotSessionType = (
  t: TranslateWithOptions,
  sessionType: string,
) =>
  ON_SITE_SESSION_TYPES.includes(sessionType)
    ? t(`Booking.TimeSlot.SessionType.${sessionType}`, {
        defaultValue: formatSessionType(t, sessionType),
      })
    : t("Booking.TimeSlot.SessionType", {
        key: upperFirst(sessionType),
      });

export const formatCheckinDate = (time: string): string => {
  const date = new Date(time);
  const now = new Date();

  if (isAfter(subDays(now, 7), date)) {
    return format(date, "MMM d, yyyy, h:mm a");
  }
  if (isAfter(subDays(now, 1), date)) {
    return format(date, "EEEE 'at' h:mm a");
  }
  return formatDistanceToNow(date, { addSuffix: true });
};

export const formatScore = (score: number | null | undefined): string => {
  if (score == null) {
    return "N/A";
  }

  return (Math.round(score * 10) / 10).toString();
};
